/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import {
  DeploymentEnvironmentName,
  environmentConfig,
  isDevelopmentNodeEnvironment,
} from "@src/appV2/environment";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { isCapacitorPlatform } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { UserLocationState } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQuery } from "@tanstack/react-query";
import constate from "constate";
import { addMinutes, isAfter } from "date-fns";
import { HyperTrack, type Subscription } from "hypertrack-sdk-ionic-capacitor";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLazyIsInsideGeofence } from "../hooks/useIsInsideGeofence";
import { orderHandleForShift } from "../providers/hypertrackLocationProvider";
import { type GeoLocation } from "../types";

const GEOFENCE_POLLING_INTERVAL_MS = 10_000;
const GEOFENCE_STATE_CHANGE_THRESHOLD_MINUTES = 5;

function isDevelopmentEnvironment(): boolean {
  return (
    isDevelopmentNodeEnvironment() ||
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.DEVELOPMENT ||
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.LOCAL ||
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.STAGING ||
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.PROD_SHADOW
  );
}

// Development helper for web testing
const WEB_GEOFENCE_DEBUG_KEY = "cbh-web-geofence-debug";
function getWebGeofenceOverride(): boolean | undefined {
  if (typeof globalThis === "undefined" || isCapacitorPlatform() || !isDevelopmentEnvironment()) {
    return undefined;
  }

  const stored = localStorage.getItem(WEB_GEOFENCE_DEBUG_KEY);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return stored ? JSON.parse(stored) : undefined;
}

function setWebGeofenceOverride(inside: boolean | undefined): void {
  if (typeof globalThis === "undefined" || isCapacitorPlatform() || !isDevelopmentEnvironment()) {
    return;
  }

  if (inside === undefined) {
    localStorage.removeItem(WEB_GEOFENCE_DEBUG_KEY);
  } else {
    localStorage.setItem(WEB_GEOFENCE_DEBUG_KEY, JSON.stringify(inside));
  }
}

/**
 * Expose debug functions globally for web testing (development mode only)
 *
 * These controls only affect the `useGeofence` hook behavior and allow overriding
 * geofence detection for testing clock-in/out workflows without physical location changes.
 *
 * Usage in browser console during development:
 * - `window.cbhGeofenceDebug.setInside(true)` - Force geofence status to "inside"
 * - `window.cbhGeofenceDebug.setOutside()` - Force geofence status to "outside"
 * - `window.cbhGeofenceDebug.clearOverride()` - Clear override and resume normal detection
 * - `window.cbhGeofenceDebug.getOverride()` - Check current override status
 *
 * Requirements:
 * - Only available in development mode (NODE_ENV=development or API environment=development/local)
 * - Only available on web platforms (not native apps)
 * - Only affects components using the `useGeofence` hook
 * - Overrides persist across page refreshes until cleared
 */
if (typeof globalThis !== "undefined" && !isCapacitorPlatform() && isDevelopmentEnvironment()) {
  (globalThis as any).cbhGeofenceDebug = {
    setInside: (inside: boolean) => {
      setWebGeofenceOverride(inside);
    },
    setOutside: () => {
      setWebGeofenceOverride(false);
    },
    clearOverride: () => {
      setWebGeofenceOverride(undefined);
    },
    getOverride: getWebGeofenceOverride,
  };
}

export interface UseGeofenceProps {
  shiftId: string;
  workerId: string;
  initialLocation: UserLocationState | undefined;
  fallbackGeofenceLocation: GeoLocation | undefined;
  enableGeofenceTracking?: boolean;
}

function useGeofence(props: UseGeofenceProps) {
  const {
    shiftId,
    workerId,
    initialLocation = UserLocationState.OUTSIDE_GEOFENCE,
    fallbackGeofenceLocation,
    enableGeofenceTracking = false,
  } = props;

  const worker = useDefinedWorker();
  const isSupportPairingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_SUPPORT_PAIRING, {
    defaultValue: false,
  });

  const isHyperTrackEnabled = useCbhFlag(CbhFeatureFlag.HYPERTRACK_CLOCK_IN_OUT, {
    defaultValue: false,
  });

  const isNative = isCapacitorPlatform();

  // Test account override: always inside when test account + support pairing enabled
  const isTestAccountOverride = worker.isTestAccount === true && isSupportPairingEnabled;

  // Determine if we can subscribe to HyperTrack based on current conditions
  const canSubscribeToHyperTrack = useMemo(() => {
    return isNative && isHyperTrackEnabled && enableGeofenceTracking;
  }, [isNative, isHyperTrackEnabled, enableGeofenceTracking]);

  const [hyperTrackFailed, setHyperTrackFailed] = useState(false);
  const [inside, setInside] = useState(
    isTestAccountOverride || initialLocation === UserLocationState.INSIDE_GEOFENCE
  );
  const lastInsideChangeTimestampRef = useRef(new Date());

  const getIsInsideGeofence = useLazyIsInsideGeofence({
    requestUniqueId: {
      workerId,
      shiftId,
    },
    action: "calculate-driving-distance",
    fallbackGeofenceLocation,
  });

  const query = useQuery({
    queryKey: ["location/geofence", { shiftId, workerId }],
    queryFn: async () => {
      const startEvent = {
        shiftId,
        workerId,
        method: "fallback_polling",
      };
      logEvent(APP_V2_USER_EVENTS.GEOFENCE_POLLING_STARTED, startEvent);

      try {
        const result = await getIsInsideGeofence();

        const completedEvent = {
          shiftId,
          workerId,
          method: "fallback_polling",
          isInsideGeofence: result?.data?.isInsideGeofence,
          success: true,
          canSubscribeToHyperTrack,
          hyperTrackFailed,
          isTestAccountOverride,
        };
        logEvent(APP_V2_USER_EVENTS.GEOFENCE_POLLING_COMPLETED, completedEvent);

        return result;
      } catch (error) {
        const errorEvent = {
          error,
          metadata: {
            shiftId,
            workerId,
            method: "fallback_polling",
          },
        };
        logError(APP_V2_APP_EVENTS.GEOFENCE_POLLING_FAILED, errorEvent);
        throw error;
      }
    },
    enabled: !isTestAccountOverride && (!canSubscribeToHyperTrack || hyperTrackFailed), // Skip polling when test account override is active
    refetchInterval: GEOFENCE_POLLING_INTERVAL_MS,
    staleTime: GEOFENCE_POLLING_INTERVAL_MS,
    cacheTime: GEOFENCE_POLLING_INTERVAL_MS,
    refetchOnMount: "always",
  });

  /**
   * In some cases when worker devices have poor GPS reception,
   * We are getting frequent changes to geofence status. These can change inside state
   * to be outside and vice versa many times each minute. This manifests to workers
   * as showing/hiding of the clock in/out buttons and effectively prevents
   * then from using the time clock.
   *
   * We are implementing a 5 minute threshold which ignores changes from inside to outside
   * to prevent issues workers are facing.
   *
   * The assumption here is that a geofence check can't be falsely set to inside if the worker
   * is far from the workplace. If geofence status is set to inside, that means the worker is
   * either inside of the workplace or very near it.
   */
  const changeInsideState = useCallback(
    (newInside: boolean, method: string) => {
      const changeToOutside = inside && !newInside;
      const isInThreshold =
        isDefined(lastInsideChangeTimestampRef.current) &&
        isAfter(
          addMinutes(lastInsideChangeTimestampRef.current, GEOFENCE_STATE_CHANGE_THRESHOLD_MINUTES),
          new Date()
        );

      if (changeToOutside && isInThreshold) {
        logEvent(APP_V2_USER_EVENTS.GEOFENCE_STATUS_CHANGED_IGNORED, {
          shiftId,
          workerId,
          method,
          previousInside: inside,
          newInside,
          canSubscribeToHyperTrack,
          hyperTrackFailed,
          isTestAccountOverride,
          lastChangeTimestamp: lastInsideChangeTimestampRef.current,
        });
        return;
      }

      if (newInside !== inside) {
        logEvent(APP_V2_USER_EVENTS.GEOFENCE_STATUS_CHANGED, {
          shiftId,
          workerId,
          method,
          previousInside: inside,
          newInside,
          queryDataExists: Boolean(query.data),
          initialLocationFallback: initialLocation === UserLocationState.INSIDE_GEOFENCE,
        });
      }

      if (newInside) {
        lastInsideChangeTimestampRef.current = new Date();
      }
      setInside(newInside);
    },
    [
      inside,
      shiftId,
      workerId,
      canSubscribeToHyperTrack,
      hyperTrackFailed,
      isTestAccountOverride,
      query.data,
      initialLocation,
    ]
  );

  useEffect(() => {
    if (isTestAccountOverride || !canSubscribeToHyperTrack || hyperTrackFailed) {
      return;
    }

    let ordersUnsubscribe: Subscription | undefined;

    async function setupSubscriptions() {
      try {
        const setupStartEvent = {
          shiftId,
          workerId,
        };
        logEvent(APP_V2_USER_EVENTS.HYPERTRACK_SUBSCRIPTION_SETUP_STARTED, setupStartEvent);

        HyperTrack.setWorkerHandle(workerId);

        ordersUnsubscribe = HyperTrack.subscribeToOrders(async (orders) => {
          const orderHandle = orderHandleForShift({ shiftId, workerId });
          const order = orders.get(orderHandle);

          const orderReceivedEvent = {
            shiftId,
            workerId,
            orderHandle,
            hasOrder: Boolean(order),
          };
          logEvent(APP_V2_USER_EVENTS.HYPERTRACK_ORDER_RECEIVED, orderReceivedEvent);

          const isInsideGeofence = await order?.isInsideGeofence();

          if (isInsideGeofence?.type !== "success") {
            const checkFailedEvent = {
              shiftId,
              workerId,
              resultType: isInsideGeofence?.type,
            };
            logEvent(APP_V2_USER_EVENTS.HYPERTRACK_GEOFENCE_CHECK_FAILED, checkFailedEvent);
            // trigger GPS fallback
            setHyperTrackFailed(true);
            return;
          }

          changeInsideState(isInsideGeofence.value, "hypertrack_subscription");
        });

        const setupCompletedEvent = {
          shiftId,
          workerId,
        };
        logEvent(APP_V2_USER_EVENTS.HYPERTRACK_SUBSCRIPTION_SETUP_COMPLETED, setupCompletedEvent);
      } catch (error) {
        // trigger GPS fallback
        setHyperTrackFailed(true);
        const setupFailedEvent = {
          error,
          metadata: {
            shiftId,
            workerId,
          },
        };
        logError(APP_V2_APP_EVENTS.HYPERTRACK_SUBSCRIPTION_SETUP_FAILED, setupFailedEvent);
      }
    }

    void setupSubscriptions();

    return function () {
      void ordersUnsubscribe?.remove();
    };
  }, [
    canSubscribeToHyperTrack,
    hyperTrackFailed,
    workerId,
    shiftId,
    inside,
    isTestAccountOverride,
    changeInsideState,
  ]);

  // Use fallback data when not using subscriptions
  useEffect(() => {
    if (isTestAccountOverride || (canSubscribeToHyperTrack && !hyperTrackFailed)) {
      return;
    }

    const webOverride = getWebGeofenceOverride();

    const fallbackInside =
      webOverride ??
      ((query.data?.data?.isInsideGeofence ?? false) ||
        initialLocation === UserLocationState.INSIDE_GEOFENCE);

    changeInsideState(
      fallbackInside,
      isDefined(webOverride) ? "web_debug_override" : "fallback_polling"
    );
  }, [
    canSubscribeToHyperTrack,
    hyperTrackFailed,
    query.data,
    query.dataUpdatedAt,
    initialLocation,
    inside,
    shiftId,
    workerId,
    isTestAccountOverride,
    changeInsideState,
  ]);

  // Refetch function to manually trigger fallback geofence check
  const refetchGeofence = useCallback(async () => {
    if (isTestAccountOverride) {
      return { inside: true };
    }

    // Only refetch if we're using fallback (not HyperTrack subscription)
    if (!canSubscribeToHyperTrack || hyperTrackFailed) {
      try {
        const result = await query.refetch();
        const newInside = result.data?.data?.isInsideGeofence ?? false;
        const error = result.error ?? (query.data?.data?.error as Error | undefined);
        return { inside: newInside, error };
      } catch (error: unknown) {
        return { inside: false, error: error as Error };
      }
    }

    // If using HyperTrack subscription, we can't manually refetch
    return { inside };
  }, [isTestAccountOverride, canSubscribeToHyperTrack, hyperTrackFailed, query, inside]);

  // Use the error from the query or the data if it exists
  const error = (query.error ?? query.data?.data?.error) as Error | undefined;
  return {
    inside,
    refetchGeofence,
    error,
  };
}

export const [GeofenceProvider, useGeofenceContext] = constate(useGeofence);
/* eslint-enable max-lines */
